<template>
  <div class="card">
    <div class="card-header">
      <b>Asset ID:</b> {{ selectedId.split('|')[0] }} <b>|</b>
      <b>Order ID:</b> {{ selectedId.split('|')[1] }}
    </div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-12 table-container">
          <table class="table table-borderless">
            <license-checker-table-headers />
            <tbody>
              <template v-if="tableData.lineItems.length">
                <tr
                  v-for="lineItem in tableData.lineItems"
                  :key="lineItem.ID"
                  style="position: relative;"
                  :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'danger' : ''"
                >
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ lineItem.ACTIVATION_ID }}
                       
                    </template>
                    <template v-else>
                      <div class="danger-cell">{{ lineItem.ACTIVATION_ID }} </div>
                      <div
                        class="success-cell"
                        :style="`margin-bottom: ${warningRowHeight[lineItem.ACTIVATION_ID]}`"
                      >
                        {{ getFaultyLineItem(lineItem.ACTIVATION_ID).ACTIVATION_ID }}&nbsp;
                        <i
                          class="fa fa-exclamation-circle faulty"
                          v-if="getFaultyLineItem(lineItem.ACTIVATION_ID).IsOrderIdMissingInFNO"
                          v-b-tooltip.hover
                          title="Line Item Not Created"
                        />
                      </div>
                      <div
                        class="warning-cell"
                        :ref="lineItem.ACTIVATION_ID"
                        v-if="!hasNoActions(lineItem.ACTIVATION_ID)"
                        :style="`margin-top: -${warningRowHeight[lineItem.ACTIVATION_ID]}`"
                      >
                        <div>
                          <ul class="actions-list">
                            <li
                              :key="item.key + i"
                              v-for="(item, i) in getFaultyRowInfo(lineItem.ACTIVATION_ID, true)"
                            >
                              <b class="warning-text">Action to perform:</b>
                              <span>{{ `${item.key}: ${item.value}` }}</span>
                              <template v-if="shouldShowSubActions(lineItem.ACTIVATION_ID, i)">
                                &nbsp;&nbsp;
                                <b class="warning-text">Sub action:</b>
                                <span>{{ `${getFaultyRowInfo(lineItem.ACTIVATION_ID)[i].key}: ${getFaultyRowInfo(lineItem.ACTIVATION_ID)[i].value}` }}</span>
                              </template>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </template>
                  </td>
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ `${lineItem.ALLOCATED_QTY}/${lineItem.SEAT_COUNT}` }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsNotAutoAllocated === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsNotAutoAllocated === 'Failure'"
                        />
                      </template>
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ `${lineItem.ALLOCATED_QTY}/${lineItem.SEAT_COUNT}` }}
                        &nbsp;
                        <i
                          class="fa fa-exclamation-circle faulty"
                          v-if="getFaultyLineItem(lineItem.ACTIVATION_ID).IsNotAutoAllocated"
                          v-b-tooltip.hover
                          title="Auto allocation issue"
                        />
                      </div>
                      <div class="success-cell">
                        {{
                          getFaultyLineItem(lineItem.ACTIVATION_ID).ALLOCATED_QTY + '/' +
                          getFaultyLineItem(lineItem.ACTIVATION_ID).SEAT_COUNT
                        }}
                      </div>
                    </template>
                  </td>
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ lineItem.FULFILLED_AMOUNT }}
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ lineItem.FULFILLED_AMOUNT }}
                      </div>
                      <div class="success-cell">
                        {{ getFaultyLineItem(lineItem.ACTIVATION_ID).FULFILLED_AMOUNT }}
                      </div>
                    </template>
                  </td>
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ lineItem.AVAILABLE_REALLOCATION_COUNT }}
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ lineItem.AVAILABLE_REALLOCATION_COUNT }}
                      </div>
                      <div class="success-cell">
                        {{ getFaultyLineItem(lineItem.ACTIVATION_ID).AVAILABLE_REALLOCATION_COUNT }}
                      </div>
                    </template>
                  </td>
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ getReadableDate(lineItem.VERSION_DATE) }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsRenewalIncorrect === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsRenewalIncorrect === 'Failure'"
                        />
                      </template>
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ getReadableDate(lineItem.VERSION_DATE) }}
                        &nbsp;
                        <i
                          class="fa fa-exclamation-circle faulty"
                          v-if="getFaultyLineItem(lineItem.ACTIVATION_ID).IsRenewalIncorrect"
                          v-b-tooltip.hover
                          title="Renewal issue"
                        />
                      </div>
                      <div class="success-cell">
                        {{ getReadableDate(getFaultyLineItem(lineItem.ACTIVATION_ID).VERSION_DATE) }}
                      </div>
                    </template>
                  </td>
                  <!-- LICPRO-833 start -->
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ getReadableDate(lineItem.EXPIRATION_DATE) }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsExpirationDateUpdated === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsExpirationDateUpdated === 'Failure'"
                        />
                      </template>
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ getReadableDate(lineItem.EXPIRATION_DATE) }}
                        &nbsp;
                        <i
                          class="fa fa-exclamation-circle faulty"
                          v-if="getFaultyLineItem(lineItem.ACTIVATION_ID).IsExpirationDateUpdated"
                          v-b-tooltip.hover
                          title="Expiration date incorrect"
                        />
                      </div>
                      <div class="success-cell">
                        {{ getReadableDate(getFaultyLineItem(lineItem.ACTIVATION_ID).EXPIRATION_DATE) }}
                      </div>
                    </template>                  
                  </td>
                  <!-- LICPRO-833 end -->
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ lineItem.ORDER_ID }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsOrderIdNotUpdated === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsOrderIdNotUpdated === 'Failure'"
                        />
                      </template>
                    </template>
                    <template v-else>
                      <div
                        :class="{
                          'danger-cell': true,
                          'text-center': getFaultyLineItem(lineItem.ACTIVATION_ID).IsOrderIdNotUpdated
                        }"
                      >
                        <span v-if="!getFaultyLineItem(lineItem.ACTIVATION_ID).IsOrderIdNotUpdated">
                          {{ lineItem.ORDER_ID }}
                        </span>
                        &nbsp;
                        <i
                          class="fa fa-exclamation-circle faulty"
                          v-if="getFaultyLineItem(lineItem.ACTIVATION_ID).IsOrderIdNotUpdated"
                          v-b-tooltip.hover
                          title="Order ID not updated"
                        />
                      </div>
                      <div class="success-cell">
                        {{ getFaultyLineItem(lineItem.ACTIVATION_ID).ORDER_ID }}
                      </div>
                    </template>
                  </td>
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ lineItem.ORDER_ID }}
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ lineItem.ORDER_ID }}
                      </div>
                      <div class="success-cell">
                        {{ getFaultyLineItem(lineItem.ACTIVATION_ID).ORDER_ID }}
                      </div>
                    </template>
                  </td>
                  <td :class="isFaultyItem(lineItem.ACTIVATION_ID) ? 'double-cell' : ''">
                    <template v-if="!isFaultyItem(lineItem.ACTIVATION_ID)">
                      {{ getLineItemStatus(lineItem.STATUS) }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsLineItemStateIncorrect === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsLineItemStateIncorrect === 'Failure'"
                        />
                      </template>
                    </template>
                    <template v-else>
                      <div class="danger-cell">
                        {{ getLineItemStatus(lineItem.STATUS) }}
                        &nbsp;
                        <i
                          class="fa fa-exclamation-circle faulty"
                          v-if="getFaultyLineItem(lineItem.ACTIVATION_ID).IsLineItemStateIncorrect"
                          v-b-tooltip.topleft.hover
                          title="Multiple line-items deployed"
                        />
                      </div>
                      <div class="success-cell">
                        {{ getLineItemStatus(getFaultyLineItem(lineItem.ACTIVATION_ID).STATUS) }}
                      </div>
                    </template>
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="lineItem in lineItemsToModify"
                  :key="lineItem.ID"
                  class="danger"
                  style="position: relative;"
                >
                  <td class="double-cell">
                    <div
                      class="success-cell line-item-to-modify"
                      :style="`margin-bottom: ${warningRowHeight[lineItem.ACTIVATION_ID]}`"
                    >
                      {{ lineItem.ACTIVATION_ID }}
                    </div>
                    <div
                      class="warning-cell"
                      :ref="lineItem.ACTIVATION_ID"
                      v-if="!hasNoActions(lineItem.ACTIVATION_ID)"
                      :style="`margin-top: -${warningRowHeight[lineItem.ACTIVATION_ID]}`"
                    >
                      <div>
                        <ul class="actions-list">
                          <li
                            :key="item.key + i"
                            v-for="(item, i) in getFaultyRowInfo(lineItem.ACTIVATION_ID, true)"
                          >
                            <b class="warning-text">Action to perform:</b>
                            <span>{{ `${item.key}: ${item.value}` }}</span>
                            <template v-if="shouldShowSubActions(lineItem.ACTIVATION_ID, i)">
                              &nbsp;&nbsp;
                              <b class="warning-text">Sub action:</b>
                              <span>{{ `${getFaultyRowInfo(lineItem.ACTIVATION_ID)[i].key}: ${getFaultyRowInfo(lineItem.ACTIVATION_ID)[i].value}` }}</span>
                            </template>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                  <td class="double-cell">
                    <div
                      class="success-cell line-item-to-modify"
                      :style="`margin-bottom: ${warningRowHeight[lineItem.ACTIVATION_ID]}`"
                    >
                      {{ `${lineItem.ALLOCATED_QTY}/${lineItem.SEAT_COUNT}` }}
                    </div>
                    <template v-if="isModified(lineItem.ACTIVATION_ID)">
                      <license-checker-check-cross
                        :check="isModified(lineItem.ACTIVATION_ID, true)
                          .ACTION_STATUS.IsNotAutoAllocated === 'Success'"
                        :cross="isModified(lineItem.ACTIVATION_ID, true)
                          .ACTION_STATUS.IsNotAutoAllocated === 'Failure'"
                      />
                    </template>
                  </td>
                  <td class="double-cell">
                    <div class="success-cell line-item-to-modify">
                      {{ lineItem.FULFILLED_AMOUNT }}
                    </div>
                  </td>
                  <td class="double-cell">
                    <div class="success-cell line-item-to-modify">
                      {{ lineItem.AVAILABLE_REALLOCATION_COUNT }}
                    </div>
                  </td>
                  <td class="double-cell">
                    <div class="success-cell line-item-to-modify">
                      {{ getReadableDate(lineItem.VERSION_DATE) }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsRenewalIncorrect === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsRenewalIncorrect === 'Failure'"
                        />
                      </template>
                    </div>
                  </td>
                  <td class="double-cell">
                    <div class="success-cell line-item-to-modify">
                      {{ lineItem.ORDER_ID }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsOrderIdNotUpdated === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsOrderIdNotUpdated === 'Failure'"
                        />
                      </template>
                    </div>
                  </td>
                  <td class="double-cell">
                    <div class="success-cell line-item-to-modify">
                      {{ lineItem.ORDER_ID }}
                    </div>
                  </td>
                  <td class="double-cell">
                    <div class="success-cell line-item-to-modify">
                      {{ getLineItemStatus(lineItem.STATUS) }}
                      <template v-if="isModified(lineItem.ACTIVATION_ID)">
                        <license-checker-check-cross
                          :check="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsLineItemStateIncorrect === 'Success'"
                          :cross="isModified(lineItem.ACTIVATION_ID, true)
                            .ACTION_STATUS.IsLineItemStateIncorrect === 'Failure'"
                        />
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import LicenseCheckerTableHeaders from './license-checker-table-headers'
import LicenseCheckerCheckCross from './license-checker-check-cross'

export default {
  name: 'LicenseCheckerTable',
  components: { LicenseCheckerTableHeaders, LicenseCheckerCheckCross },
  data: () => ({
    warningRowHeight: {},
    warningRowToggleMap: {}
  }),
  mounted () {
    this.setHeight()
  },
  watch: {
    selectedId () {
      this.$nextTick(this.setHeight)
    }
  },
  props: {
    selectedId: { type: String, required: true },
    tableData: { type: Object, default: () => ({}) },
    lineItemsToModify: { type: Object, default: () => ({}) },
    modalData: { type: Array, default: () => ([]) }
  },
  methods: {
    setHeight () {
      Object.keys(this.$refs).forEach((key) => {
        if (this.$refs[key][0]) {
          this.warningRowHeight[key] = this.$refs[key][0].clientHeight + 'px'
        }
      })
      this.$forceUpdate()
    },
    getReadableDate (date) {
      return moment(date).format('YYYY-MM-DD')
    },
    isFaultyItem (id) {
      return Object.keys(this.lineItemsToModify).includes(id)
    },
    getLineItemStatus (status) {
      const [firstChar, ...rest] = status.replace('bo.constants.states.', '')
      return `${firstChar.toUpperCase()}${rest.join('')}`
    },
    getFaultyLineItem (id) {
      return this.lineItemsToModify[id]
    },
    getFaultyRowInfo (id, action = false) {
      const item = this.getFaultyLineItem(id)
      return action
        ? Object.keys(item.ACTION_TO_PERFORM || {}).map((key) => {
          return { key, value: item.ACTION_TO_PERFORM[key] }
        })
        : Object.keys(item.SUB_ACTION || {}).map((key) => {
          return { key, value: item.SUB_ACTION[key] }
        })
    },
    hasActions (id, action = false) {
      const string = this.getFaultyRowInfo(id, action)
      return string !== ''
    },
    hasNoActions (id) {
      return !this.hasActions(id, true) && !this.hasActions(id)
    },
    isModified (id, getItem = false) {
      const item = this.modalData.find(a => a.ACTIVATION_ID === id)
      return getItem ? item : !!item
    },
    shouldShowSubActions (id, index) {
      const { key, value } = this.getFaultyRowInfo(id)[index]
      return key && value
    }
  }
}
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../styles/variables.scss';

.table-container {
  padding: 20px 15px;
}

.table {
  border-collapse: separate;
  border-spacing: 0 0.6rem;
}

.table tr {
  position: relative;
}

.table td:not(.double-cell) {
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

.table td.double-cell {
  border: none;
}

.table tr th:first-child,
.table tr td:first-child:not(.double-cell) {
  border-left: 1px solid #dee2e6;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
  padding-left: 1rem;
}

.table tr.success td:not(.double-cell):first-child {
  border-left: 5px solid $success;
}

.table tr.danger td:not(.double-cell):first-child {
  border-left: 5px solid $danger;
}

.table tr th:last-child,
.table tr td:last-child {
  border-right: 1px solid #dee2e6;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
}

td.double-cell {
  padding: 0;
  background-color: $white;
}

.double-cell .danger-cell,
.double-cell .success-cell {
  padding: 0.50rem;
}

.success-cell {
  border-bottom: 1px solid #dee2e6;
  color: $success;
}

.table td.double-cell:first-child {
  border-left: 1px solid #dee2e6;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.table td.double-cell:last-child {
  border-right: 1px solid #dee2e6;
  border-top-right-radius: 5px;
}

.double-cell:first-child .success-cell {
  border-left: 5px solid $success;
}

.danger-cell {
  border-top: 1px solid #dee2e6;
  color: $danger;
  border-bottom: 1px solid #dee2e6;
}

.danger-cell p {
  margin-bottom: 0;
}

.danger-cell .flag {
  font-size: 0.6rem;
}

.double-cell:first-child .danger-cell {
  border-left: 5px solid $danger;
  border-top-left-radius: 5px;
}

.double-cell:first-child .warning-cell > div {
  border-bottom-left-radius: 5px;
  margin-left: 0;
}

.warning-cell {
  position: absolute;
  width: 100%;
  left: 1px;
  right: 1px;
  padding: 0 17px 0 15px;
}

.warning-cell > div {
  padding: 0.50rem;
  border-left: 5px solid $warning;
  border-bottom: 1px solid #dee2e6;
  // border-right: 1px solid #dee2e6;
  // border-bottom-right-radius: 5px;
}

.warning-cell p {
  margin-bottom: 0;
}

.warning-text {
  color: $warning;
}

.danger-cell .faulty {
  color: $danger;
  border-bottom: 1px dotted $danger;
  display: inline-block;
  padding-bottom: 2px;
}

.btn.btn-primary.fix-issue-btn {
  border-radius: 5px;
  padding-left: 20px;
  padding-right: 20px;
}

.white-text {
  color: $white;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.actions-list {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.check-mark::after {
  content: '\2713';
  color: $success;
}

.cross-mark::after {
  content: '\2717';
  color: $danger;
}

.success-cell.line-item-to-modify {
  border-top: 1px solid #dee2e6;
}

.double-cell:first-child .success-cell.line-item-to-modify {
  border-top-left-radius: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/license-checker/license-checker-table.vue